import { IItemRightsCollection, RightType } from "Global/Base/Rights/RightTypes";
import { IValidationDataObject } from "OW.Remoting/Remoting/DataTypes/IValidationDataObject";
import { Localization } from "OW.Remoting/Remoting/Localization";
import { ErrorHandler } from "UI/Errors";
import { HeaderTemplate } from "UI/UI.Controls/DataGridSubcontrols/Templates/HeaderTemplate";
import { ComponentRightsExtractor } from "UI/UI/Rights/ComponentRightsExtractor";
import { ControlRightTypesGroup } from "UI/UI/Rights/RightGroups";
import { Rights } from "UI/UI/Rights/Rights";
import { Event } from "../../../../Global/Base/Events";
import { AsyncLock, Releaser } from "../../../../Global/Base/Promises/AsyncLock";
import { DeferredStateful, PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { ValidationErrorType } from "../../../../OW.Remoting/Basic/EOWExceptionsInterfaces";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList } from "../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { IValidatable } from "../../../UI.BaseControls/ControlInterfaces";
import { ActionType } from "../../../UI.Controls/Actions/IAction";
import { CompactDataGrid } from "../../../UI.Controls/CompactDatagrid";
import { Column } from "../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { HeaderRow } from "../../../UI.Controls/DataGridSubcontrols/Components/HeaderRow";
import { IEditableListDataRowElement } from "../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import {
    EditableDBDataSource,
    IActualObjectChangeEventArgs,
    INewRow,
    SaveResultData,
} from "../../../UI.Controls/DataSource/EditableDBDataSource";
import { NPDataSource } from "../../../UI.Controls/DataSource/NPDataSource";
import { EditableDataGrid } from "../../../UI.Controls/EditableDatagrid";
import { IEditableListDataGrid } from "../../../UI.Controls/IDataGrid";
import { IDataGridSelectionChangedEvent } from "../../../UI.Controls/IEvent";
import { Control } from "../Controls/Control";
import {
    FormControlWithLoggerParams,
    IAttributeControl,
    IAttrNameControl,
    IRequiresActualObject,
} from "../Controls/ControlInterfaces";
import { FormUICustomProperty } from "../FormUI/FormUI";
import { FrameColumnInfoAttributes } from "../IFrameColumnInfo";
import { MessageButtonType } from "../MessageWindow";
import { UIManager } from "../UIManager";
import { IValidatableFrame, IValidationMaster } from "./FormControl";
import { HideButtons } from "./Frames";
import { EditStyleType, IMFNoChangeCheck, IObjectListEdit } from "./Frames/FrameInterfaces";
import { FrameSharedMethods } from "./Frames/FrameSharedMethods";
import { GenericListFrame } from "./Frames/GenericListFrame";
import { ObjectListEditRightsEvaluationOverrides } from "./ObjectListEditRightsEvaluationOverrides";
import { SharedMethods } from "./SharedMethods";
import { IValidationData, ValidationManager } from "./ValidationManager";

export class ObjectListEdit extends GenericListFrame implements IValidatableFrame, IRequiresActualObject, IAttrNameControl, IMFNoChangeCheck, IObjectListEdit {
    //#region Events
    public valueChanged: Event<ObjectListEdit, any> = new Event<ObjectListEdit, any>();
    //#endregion Events

    //#region Listeners
    //#endregion Listeners

    protected override __rightsEvaluationOverrides: ObjectListEditRightsEvaluationOverrides;
    //#region Fields
    public isOLE: boolean = true;
    public attrName: string;
    private _controlValue: any;
    public override dataSource: NPDataSource;
    public override dataGrid: IEditableListDataGrid<IEditableListDataRowElement>;
    private _itemsRightReadOnly: IItemRightsCollection;
    private _newRecordCreatingLock: AsyncLock = new AsyncLock();
    public readonly imfNoChangeCheck: boolean = false;
    private _newRecordDeferred: DeferredStateful<void>;
    private _dataSourceValueChangedListenerPromise: () => Promise<void> | undefined = undefined;
    private _itemsRightRequired: IItemRightsCollection;
    private _itemsRightPostPoneRequired: IItemRightsCollection;
    public override validationManager: ValidationManager = new ValidationManager();
    //#endregion Fields

    //#region Properties

    /** Objekt, nad kterým se vyhodnocují dynamické sloupce */
    protected override get _dynamicColumnsObject(): OWObject | null {
        return this.actualObject;
    }

    public override set headerColor(value: string) {
        if (this._color && this._color.length > 0)
            this.element.classList.remove(this._color);

        this.element.classList.add(value);

        this._color = value;

        if (this.dataGrid && this.dataGrid.headerRow && this.dataGrid.headerRow instanceof HeaderRow) // Nepoužívá se interface, neb se přistupuje přímo na vlastnosti této konkrétní třídy
            this.dataGrid.headerRow.color = value;

        if (this.menu)
            this.menu.setProperty(FormUICustomProperty.pageColor, value);
    }

    public override get headerColor(): string {
        return this._color; //this.dataGrid.Header.Color;
    }

    //#region ActualObject
    //Aktualni objekt na detailu - master objektu na LOV
    public override set actualObject(value: OWObject) {
        if (this._actualObject === value)
            return;

        this._events.removeByKey("aoListAttrChanged", "aoAttrChanged");

        if (value != null) {
            this._events.addWithKey("aoAttrChanged", value.attributeChanged, (_, attrName) => {
                if (attrName !== this.attrName) {
                    this._waitForColumnLoad().then(() => this._requestDynamicColumnsUpdate());
                }
            }, { removePrevious: true });

            this._setActualObject(value);

            // Pokud se používá stejný objekt jako na masterFramu, tak se použije i jeho validateManager.
            if (this.masterFrame.actualObject === value && this.masterFrame instanceof IValidationMaster) {
                this.validationManager = this.masterFrame.validationManager;
            } else {
                this.validationManager = new ValidationManager();
            }

            this._waitForColumnLoad().then(() => this._requestDynamicColumnsUpdate());

            value.getValue(this.attrName).then(async (objList: OWObjectList) => {
                //Aktualizace stavajicich záznamů
                this.dataSource.actualizeData(objList);
                //Počáteční nastavení z nastaveného objektu.
                this.controlValue = objList;

                this._events.addWithKey("aoListAttrChanged", value.attributeChanged, async (_, attrName) => {
                    if (!StringUtils.compareCaseInsensitive(attrName, this.attrName))
                        return;

                    const objList = await value.getValue(this.attrName) as OWObjectList;
                    if (objList != null && objList.count === this.dataSource.objectList.count) {
                        if (objList.count === 0)
                            return;

                        let updateReq = false;
                        for (const oii of objList.items) {
                            if (!this.dataSource.objectList.items.find(item => item.compare(oii))) {
                                updateReq = true;
                                break;
                            }
                        }
                        if (!updateReq)
                            return;
                    }
                    this.dataSource.actualizeData(objList);
                });
            });
        } else {
            this._setActualObject(null);
            this.controlValue = null;
        }
    }

    //Aktualni objekt na detailu - master objektu na LOV
    public override get actualObject(): OWObject {
        return this._actualObject;
    }

    public set controlValue(value: OWObjectList) {
        this._controlValue = value;
    }

    public get controlValue(): OWObjectList {
        return this._controlValue;
    }

    public override set enabled(value: boolean) {
        super.enabled = value;
        if (this.isActive)
            this.isEditing = this.isActive; // OLE je automaticky v editaci, pokud je povolený právy - ale nejde mu zrušit mód editace
    }

    public override get enabled(): boolean {
        return super.enabled;
    }

    public override set interactive(interactive: boolean) {
        super.interactive = interactive;
        if (this.isActive)
            this.isEditing = this.isActive; // OLE je automaticky v editaci, pokud je povolený právy - ale nejde mu zrušit mód editace
    }

    public override get interactive(): boolean {
        return super.interactive;
    }

    public override get readVersioned(): boolean | null {
        return this._readVersioned;
    }

    public override set readVersioned(value: boolean) {
        this._readVersioned = value;
    }

    /**
     * Přepsaná metoda MasterableFrame. Protože je zde actualObject dostupný "pořád",
     * kontrolujeme označení řádku. Používá se při vyhodnocování práv.
     */
    public override get hasSelectedRecord(): boolean {
        return this.currentDataGrid && this.currentDataGrid.selectedRows.length > 0;
    }
    //#endregion Properties

    protected override async _saveRecords(): Promise<boolean> {
        return Promise.resolve(true);
    }

    //#endregion ActualObject

    //#region IValidatable
    /**
     * Spouští validaci seznamu
     */
    public override async validate(checkRecommended?: boolean, checkPostponed?: boolean): Promise<boolean> {
        if (!this.visible) {
            return true;
        }
        await this.waitForPendingUpdates();
        const required = await this._evaluateRequired();
        const postPoneRequired = await this._evaluatePostPoneRequired();

        if ((required || (postPoneRequired && checkPostponed)) && !this.controlValue?.items.length) {
            this.validationManager.addComponentError(this.actualObject as any as IValidationDataObject<IValidationData>, {
                type: ValidationErrorType.requiredError,
                errorMessage: await Localization.getTextPromise("GUI-1000"), // "Nevyplněné povinné pole"
            },
            {
                control: null,
                attributeName: this.attrName,
                container: null,
                showsInvalidData: () => false,
            });
            return false;
        } else {
            if (this.validationManager.hasErrors) {
                this.validationManager.clearErrors(this.actualObject.id);
            }
        }

        await super.validate(checkRecommended);
        //TODO: az se opravi na predkovi tak se bude vracet hodnota primo z tama
        return !this.validationManager.hasErrors;
    }

    public override async validateAndShowMessage(checkRecommended?: boolean, checkPostponed?: boolean): Promise<boolean> {
        if (await this.validate(checkRecommended, checkPostponed)) {
            return true;
        }

        await SharedMethods.showValidationMessages(this.validationManager);

        return false;
    }
    //#endregion IValidatable

    //#region Constructors
    constructor(params: FormControlWithLoggerParams) {
        super(params);

        this.attrName = this.element.getAttribute("data-attrname");
        if (!this.attrName) {
            this._logger.error("The attrName is not provided in definition of the component.");
        }
        this.masterAttr = this.element.getAttribute("data-masterattr");
        this.editStyle = EditStyleType.esMulti;

        this.element.classList.add("dataGrid", "ObjectListEdit");

        this.isEditable = true;
        this._itemsRightReadOnly = ComponentRightsExtractor.loadRightsForTypes(this, RightType.enabled);
        this._itemsRightRequired = ComponentRightsExtractor.loadRightsForTypes(this, RightType.required);
        this._itemsRightPostPoneRequired = ComponentRightsExtractor.loadRightsForTypes(this, RightType.postponedRequired);

        // nad instancemi neumíme provádět řazení
        this.disableSort = true;
        this._showAuditData = false;
    }

    //#endregion Constructors

    //#region Methods

    protected override _initializeListeners(): void {
        super._initializeListeners();

        // zakazani/povoleni udalosti valueChanged
        this.canValueLoadChanged.add((_sender: Control, value: boolean) => {
            this.valueChanged.enabled = value;
        });

        // data se načítají až po vytvoření datagridu
        this.dataGridInitialized.add(() => {
            this.dataSource.loadNewData(false, this._cancelPrevAndCreateDataLoadingCTS().token);
        });
    }

    protected override async _initializeDataSource(): Promise<void> {
        this.dataSource = new NPDataSource({
            className: this.objectName,
            frameContext: this.frameContext,
        });

        this.dataSource.setValueChanged.add((_sender, value) => {
            this.enqueuePendingUpdateTask(value);
        });

        this.dataSource.valueChanged.add((_sender, value) => {
            this._dataSourceValueChangedListenerPromise = async () => {
                this.controlValue = value;
                this.valueChanged.trigger(this, { Sender: this });
            };

            //V případě nového záznamu je potřeba tohle volat později, až se počká na vše ostatní. Docházelo totiž k tomu, že na trigerovaný event reaguje frame, který načte nová data a mohlo se stát, že nově přidávaný záznam ještě nebyl uložen a když se pak s ním mělo pracovat, už měl data refreshnutá framem, která záznam neobsahovala.
            if (this._newRecordDeferred && !this._newRecordDeferred.isSettled) {
                return;
            }
            //v opačném případě se může promise zavolat rovnou a nastaví ho undefined, aby bylo možné poznat, že už proběhl
            this._dataSourceValueChangedListenerPromise();
            this._dataSourceValueChangedListenerPromise = undefined;
        });

        this.dataSource.loadingStarted.add(() => {
            UIManager.instance.showLoader();
        });
        this.dataSource.loadingEnded.add(() => {
            UIManager.instance.hideLoader();
        });
        this.dataSource.attributesChanged.add(async (_senderDataSource: EditableDBDataSource, eventData: IActualObjectChangeEventArgs) => {
            if (eventData.context) {
                this._evaluateAttributeChangedRightsThrottledWithContext(eventData.context);
            }
        });
    }

    protected override async _initializeDataGrid(): Promise<void> {
        await this._createAndInitDataGrid();

        this.dataGrid.showFrameMenu = true;
        this.compactDataGrid.showFrameMenu = true;

        this.dataGrid.cellValidityChanged.add(async (sender, data) => {
            const rowObj = await this.dataSource.getObject(data.row.dataKey);
            await this.waitForPendingUpdates();
            this._cellValidityChanged(sender as IValidatable & IAttributeControl, rowObj, data.validity);
        });

        this.dataGrid.permissionsChanged.add((sender, data) => {
            this._rowPermissionsChanged(sender.dataKey, data);
        });

        this._isDataGridInitialized = true;
        this.dataGridInitialized.trigger(this, this.dataGrid);
    }

    protected override async _createAndInitDataGrid(): Promise<void> {
        this.dataGrid = new EditableDataGrid();
        this.compactDataGrid = new CompactDataGrid();

        if (this.dataGrid.emptyMessage) {
            this.dataGrid.emptyMessage.emptyMessageCustomization = this.emptyMessageCustomization;
        }

        if (this.compactDataGrid.emptyMessage) {
            this.compactDataGrid.emptyMessage.emptyMessageCustomization = this.emptyMessageCustomization;
        }

        this._setCurrentDataGrid(this.dataGrid);

        this.dataGrid.dataSource = this.dataSource;
        this.dataGrid.objectName = this.objectName;
        this.compactDataGrid.objectName = this.objectName;

        this.dataGrid.addEventListener("SelectionChanged", (e: IDataGridSelectionChangedEvent) => {
            this.onSelectionChanged(e);
        });
        await this._createColumns(FrameSharedMethods.getColumnsDefinition(this.element));
        await this._updateDynamicColumns();
        this._columnLoadingFinished.resolve();

        this.dataGrid.addEventListener("resizablestop", (e) => {
            const ht = e.target as HeaderTemplate;
            const width = ht.parentColumn.width;
            this._setProfileColumnInfoAttr(ht.parentColumn.attrName, FrameColumnInfoAttributes.width, width);
            this.frameProfileChanged.trigger(this, null);
        });

        this.dataGrid.fetchCount = 1000;
        this.dataGrid.displayEditedCountBar = false;
        this.dataGrid.dontKeepChangedRecords = true;
        this.container.appendChild(this.dataGrid);

        this.canValueLoadChanged.add((_s: Control, val: boolean) => {
            // Skryvani a objevovani zalozky pri vstupu na frame
            if (!this.hideToolBar && this.menu) //pokud mam schovat toolbar
                this.menu.setVisibility(val && this.visible);
        });

        this._createDesktopDropDownMenu();
    }

    /**
     * Vraci hodnotu readOnly
     */
    protected async _evaluateReadOnlyIf(): Promise<boolean> {
        if (this._itemsRightReadOnly.enabled) {
            const rightResult = await Rights.evaluateSpecificControlRights(this._itemsRightReadOnly, this._itemsRightReadOnly.enabled, this.actualObject);
            return !rightResult.Enabled;
        }
        return false;
    }

    /**
     * Vraci hodnotu required
     */
    protected async _evaluateRequired(): Promise<boolean> {
        if (this._itemsRightRequired.required) {
            const rightResult = await Rights.evaluateSpecificControlRights(this._itemsRightRequired, this._itemsRightRequired.required, this.actualObject);
            return rightResult.Required;
        }
        return false;
    }

    /**
     * Vraci hodnotu postponedRequired
     */
    protected async _evaluatePostPoneRequired(): Promise<boolean> {
        if (this._itemsRightPostPoneRequired.postponedRequired) {
            const rightResult = await Rights.evaluateSpecificControlRights(this._itemsRightPostPoneRequired, this._itemsRightPostPoneRequired.postponedRequired, this.actualObject);
            return rightResult.PostponedRequired;
        }
        return false;
    }

    /**
     * Přepsáno, GenericListFrame přidává další listener na dataGridInitialized, který ObjectListEdit nepotřebuje
     */
    protected override _loadFrameData(): void {
        // do nothing.
    }

    /**
     * Metoda pro kopii zaznamu
     */
    public override async createCopy(): Promise<boolean> {
        return await this.createNew(true);
    }

    /**
     * Metoda pro kopii zaznamu
     */
    public async createNew(viaCopy: boolean = false): Promise<boolean> {
        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
            return false;

        let releaser: Releaser;
        try {
            UIManager.instance.showLoader();
            releaser = await this._newRecordCreatingLock.lock();
        } finally {
            UIManager.instance.hideLoader();
        }

        await this.actualObject.waitForPendingUpdates();

        this._newRecordDeferred = PromiseUtils.deferStateful<void>();
        this.actualObject.enqueuePendingUpdateTask(this._newRecordDeferred.promise);

        try {
            if (this.dataGrid.selectedRows.length > 1) {
                const firstSelect = this.dataGrid.selectedRows[0];
                this.dataGrid.clearSelectedRows();
                firstSelect.select();
            }
            let objRow: INewRow;
            if (viaCopy) {
                objRow = await this.dataSource.copyRow(this.dataGrid.selectedRows[0].dataKey);
            } else {
                const masterObjIDForLinkAttr = this.actualObject != undefined ? this.actualObject.instanceID : null;
                objRow = await this.dataSource.newRow(await FrameSharedMethods.createAfterCreateExtendedOperations({ ownerForm: this.ownerForm, masterFrame: this.masterFrame, afterGuiCreateMethod: this.afterGuiCreateMethod, afterGuiCreateFill: this.afterGuiCreateFill, masterLinkToAttr: this.masterLinkToAttr, masterAttr: this.masterAttr, isMainFrame: this.isMainFrame, masterObjectInstanceIDForLinkToAttr: masterObjIDForLinkAttr }, true));
            }
            const obj = objRow.actualObject;

            const row = this.currentDataGrid.selectedRows[0];
            if (row)
                (row as HTMLElement).dataset["instanceid"] = obj.instanceID;
        } catch (err) {
            ErrorHandler.instance.handleError(err);
            return false;
        } finally {
            if (this._dataSourceValueChangedListenerPromise)
                await this._dataSourceValueChangedListenerPromise();
            if (releaser)
                releaser.release();
            this._newRecordDeferred.resolve();
        }

        return true;
    }

    protected override async _addStandartActions(_isMainFrame: boolean): Promise<void> {
        const hideButtons: HideButtons = FrameSharedMethods.parseHideButtons(this.ownerForm, this.element.getAttribute("data-hidebuttons"), this.isMainFrame);

        if (!hideButtons.insert) {
            const xgaction = this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardNewRecord, async () => {
                this.newRecord();
            }, this));
            xgaction.enabled = false; //toto je potreba na zacatku nastavit na false//TODO: MV - bohuzel nestaci to nastavit na action, protoze button se vytvari az v metode addaction...
        }
        if (!hideButtons.copy) {
            this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardCopyRecord, async () => {
                this.createCopy();
            }, this));
        }

        if (!hideButtons.delete) {
            this.menu.addAction(FrameSharedMethods.createStandardAction(ActionType.standardDeleteRecord, async () => {
                this.deleteRecord();
            }, this));
        }
    }

    /**
     * Přidání akcí frame (nastavení sloupců), které nejsou v toolbaru. Jejich registrace tak nepodlehá nastavení vlastnosti hideToolBar.
    */
    protected override async _addFrameChoiceActions(): Promise<void> {
        if (this.allowColumnSettings) {
            this.menu.addAction(this._createColumnSettingsAction());
        }
    }

    protected override _createOrGetRightsOverrides(): ObjectListEditRightsEvaluationOverrides {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new ObjectListEditRightsEvaluationOverrides(this);

        return this.__rightsEvaluationOverrides;
    }

    /**
     * Metoda pro zalozeni noveho zaznamu
     */
    public override async newRecord(_objectName?: string): Promise<boolean> {
        return await this.createNew();
    }

    /**
     * Metoda pro rozeditovani zaznamu
     */
    public override async editRecord(): Promise<boolean> {
        if (this.actualObject) {
            //nastaví flag, že je objekt rozeditován. Narozdíl od isChanged, ale nemusí mít změněnou žádnou hodnotu.
            this.editingRow.trigger(this, this.actualObject);
        }

        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        return await this.ownerForm.checkAndSaveForm({
            forceSave: this._forceAutoSave,
            skipSavingEditContextId: this.useMasterContext,
        });
    }

    /**
     * Metoda pro smazani zaznamu
     */
    public override async deleteRecord(): Promise<boolean> {
        // zkontroluje se uložení změn na masteru a jeho nadřízených framech
        if (!(await this.ownerForm.checkAndSaveForm({ forceSave: this._forceAutoSave, skipSavingEditContextId: this.useMasterContext })))
            return false;

        await this.createContext();

        const res = await this._confirmDeleteDialog();
        if (res.result === MessageButtonType.ok) {
            this.validationManager.clearErrors(this.dataGrid.selectedRows.map(i => i.dataKey));
            await this.dataSource.deleteObjectsByInstanceID(this.dataGrid.selectedRows.map(i => i.dataKey));
            return true;
        }
        return false;
    }

    /**
     * Metoda pro zruseni zmen
     */
    public override async cancelChanges(): Promise<void> {
        // do nothing.
    }

    /**
     * Znovunacteni dat do seznamu
     * manualRefresh - jestli je akce vyvolana uzivatelem (zabranuje nebo povoleuju oznaceni radku v seznamu)
     */
    public override async refreshData(): Promise<void> {
        // nemá jít refreshovat
    }

    /**
     * Zakazuje a povoluje tlacitka v toolbaru podle toho jestli je nebo neni v rezimu editace
     */
    public override setToolbarToEditMode(_inEdit: boolean): void {
        // změna režimu editace nepřidává ani neskrývá žádné akce na toolbaru
    }

    public override async onSelectionChanged(evt: IDataGridSelectionChangedEvent): Promise<void> {
        if (evt.detail.data.newRowFirstSelect) {
            await this._validRowOnChange(true);
        } else {
            await this._validRowOnChange();
        }

        const context = (evt.detail.rowID !== "-1") ? await this._getObject(evt.detail.rowID) ?? undefined : undefined;
        await this._newEvaluateRightsGroup(ControlRightTypesGroup.IED | ControlRightTypesGroup.selectionChanged, context);
        if (this.dataGrid && this.dataGrid.selectedRows && this.dataGrid.selectedRows.length === 1) {
            this.contextActionsReady.trigger(this, this.dataGrid.selectedRows[0].dataKey);
        }
    }

    public override registerHeaderClickEvent(_col: Column): void {
        // do nothing.
    }

    public override async refreshOnClick(): Promise<void> {
        // OLE nemá refresh
    }

    public override saveRecords(_id?: string, _checkRecommended?: boolean): Promise<SaveResultData[]> {
        // nemá význam
        throw new Error("Method not implemented.");
    }

    protected override async _updateDynamicColumns(): Promise<void> {
        try {
            UIManager.instance.showLoader();
            await super._updateDynamicColumns();
            await this._updateRowsValues();
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private async _updateRowsValues(): Promise<void> {
        const promises = this.currentDataGrid.dataRows.map(row => this.dataSource.getValues(row.dataKey));
        await Promise.all(promises);
    }
}
